import { Injectable, NgZone, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationStart, Router, Scroll } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Back/forward returns the visitor to where they were (MV-390).
 *
 * The router (scrollPositionRestoration: 'enabled', app.config.ts) restores the
 * saved position right after the page renders. Pages that load their content
 * over HTTP (partners, city, clinic lists…) are still short at that moment, so
 * the browser clamps the scroll near the top. This service finishes the job:
 * if the saved position was not reached, it waits for the page to grow and
 * scrolls again. It gives up after MAX_WAIT_MS, on the next navigation, or as
 * soon as the visitor scrolls, taps or uses the keyboard themselves.
 */
@Injectable({ providedIn: 'root' })
export class ScrollRestorationService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly doc = inject(DOCUMENT);

  private static readonly MAX_WAIT_MS = 3000;
  private cancel: (() => void) | null = null;

  init(): void {
    const win = this.doc.defaultView;
    if (!win || typeof ResizeObserver === 'undefined') return;

    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => this.stop());

    this.router.events
      .pipe(filter((e): e is Scroll => e instanceof Scroll && !!e.position))
      .subscribe(e => this.zone.runOutsideAngular(() => this.restore(win, e.position!)));
  }

  private restore(win: Window, [x, y]: [number, number]): void {
    this.stop();
    if (Math.abs(win.scrollY - y) < 2) return; // router already got there

    const tryScroll = (): boolean => {
      const maxY = this.doc.documentElement.scrollHeight - win.innerHeight;
      if (maxY + 1 < y) return false; // page still too short
      win.scrollTo({ left: x, top: y, behavior: 'instant' as ScrollBehavior });
      return true;
    };

    const observer = new ResizeObserver(() => { if (tryScroll()) this.stop(); });
    const timer = win.setTimeout(() => this.stop(), ScrollRestorationService.MAX_WAIT_MS);
    const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    const onUser = () => this.stop();

    this.cancel = () => {
      observer.disconnect();
      win.clearTimeout(timer);
      userEvents.forEach(t => win.removeEventListener(t, onUser));
    };
    observer.observe(this.doc.body);
    userEvents.forEach(t => win.addEventListener(t, onUser, { passive: true }));
  }

  private stop(): void {
    this.cancel?.();
    this.cancel = null;
  }
}
